'use client';

import type { NewsletterSubscribeRequest } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';

export function Newsletter() {
  const { t } = useTranslations();
  const emailLabel = t('text183');

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (request: NewsletterSubscribeRequest) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(request);
    },
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('', 'ea-homepage-newsletter')}>
      <form
        className={cn(
          'flex w-full flex-col items-start justify-start gap-8',
          'ea-homepage-newsletter__form',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-start justify-start gap-4">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">{t('text87')}</h2>
          <div className="ea-html-description ea-html-description--base text-white">
            <p>
              Prihláste sa na odber noviniek a získajte prehľad o nových ponukách a praktických
              informáciách z realitného trhu.
            </p>
          </div>
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row sm:justify-between">
            <div className={cn('flex h-14 w-full flex-row items-center')}>
              <Input
                aria-label={emailLabel && emailLabel !== 'text183' ? emailLabel : 'E-mail'}
                id="newsletter-email"
                type="email"
                name="email"
                placeholder={t('text183')}
                onChange={e => setEmail(e.target.value)}
                className={{
                  wrapper: cn(
                    'h-14 border-none bg-[#F3F3F31A] text-white/90 placeholder:text-white/90',
                    'ea-homepage-newsletter__form-item-email-input',
                  ),
                }}
              />
            </div>
            <Button
              type="submit"
              className={cn(
                '!h-14 w-full bg-white/90 text-secondary hover:bg-white sm:w-fit',
                'ea-homepage-newsletter__form-item-email-submit-button',
              )}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
            >
              {t('text182')}
            </Button>
          </div>

          {isSubscribed && (
            <span className={cn('font-semibold text-secondary')}>{t('text234')}</span>
          )}
          <div
            className={cn(
              'flex flex-row items-center space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id="newsletter-gdpr"
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor="newsletter-gdpr"
              className={cn(
                'text-white/50',
                'ea-homepage-newsletter__form-item-gdpr-checkbox-label',
              )}
            >
              <span className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}>
                {t('text264')}&nbsp;
                <a
                  href={t('text391') ?? '#'}
                  className={cn(
                    'text-white/50',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                  )}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392') ?? '#'}
                  className={cn(
                    'text-white/50',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                  )}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
